import { useEffect, useState } from "react";
import type { MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import type { LivestockInventoryReport } from "@/api/types";
import "./ReportsLivestockInventory.css";

type ReportStatus = "loading" | "ready" | "error";

/**
 * Livestock inventory — Reports' CURRENT-snapshot roster: active/sold/deceased/culled
 * head counts per herd, plus each bucket's own most recent transaction date. ef-052-5,
 * ADR 0009 §6 ("Reports is one picker" — a report renders inside the template at
 * `/reports/{slug}`, no area tab strip of its own; "All reports" below is the only way
 * back).
 *
 * <p>
 * D4 — NO DATE RANGE, NO APPLY BUTTON. Unlike every other report this wave
 * (`ReportsProductionSales.tsx`, `ReportsScheduleF.tsx`, ...), this is a current snapshot
 * only — there is no window to choose, so it fetches on mount instead of waiting for a
 * submit, same "no guessed window vs. no window at all" distinction the build brief's own
 * D4 note draws.
 * </p>
 *
 * <p>
 * NO ENTITY PICKER — same "no fake buttons" call `ReportsProductionSales.tsx`'s own
 * remarks make: `LivestockInventoryReportEndpoints` resolves the tenant's own
 * single/oldest entity when `entityId` is omitted, which is every farm this booth demo
 * models.
 * </p>
 */
export function ReportsLivestockInventory() {
  const [report, setReport] = useState<LivestockInventoryReport | null>(null);
  const [status, setStatus] = useState<ReportStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load(): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const result = await demoApi.getLivestockInventoryReport(undefined, controller.signal);
        if (cancelled) return;
        setReport(result);
        setStatus("ready");
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setReport(null);
        setStatus("error");
        setError(problemMessage(err, "Could not load the livestock inventory report."));
      }
    }

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  /**
   * The export is its own request, fired on the click — not fetched alongside the report,
   * same reasoning `ReportsProductionSales.tsx`'s own identical download gives.
   */
  const [csvState, setCsvState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number } | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadCsv(): Promise<void> {
    setCsvState({ kind: "busy" });
    try {
      const csv = await demoApi.getLivestockInventoryCsv();

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline ReportsProductionSales.tsx's identical
      // download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "easyfarm-livestock-inventory.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setCsvState({ kind: "done", rows });
    } catch (err) {
      setCsvState({
        kind: "error",
        message: problemMessage(err, "Could not build the livestock inventory file."),
      });
    }
  }

  function handleAllReportsClick(event: MouseEvent<HTMLAnchorElement>): void {
    event.preventDefault();
    // Self-contained, same device AreaTabs.tsx's own handleNavClick already uses rather
    // than threading a `navigate` prop through every screen this job touches: pushState
    // doesn't fire popstate on its own, so App.tsx's existing listener is told directly.
    window.history.pushState({}, "", "/reports");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  const rows = report?.rows ?? [];
  const isEmpty = status === "ready" && rows.length === 0;

  return (
    <main className="livestock-inventory">
      <PageHeader
        title="Livestock inventory"
        subtitle="Current head counts by herd and status, as of right now"
      />

      <a href="/reports" className="livestock-inventory__all-reports" onClick={handleAllReportsClick}>
        ← All reports
      </a>

      <section
        className="livestock-inventory__panel"
        aria-labelledby="livestock-inventory-heading"
        data-state={isEmpty ? "empty" : status}
      >
        <h2 id="livestock-inventory-heading" className="livestock-inventory__heading">
          Current snapshot
        </h2>

        {status === "loading" && (
          <StateMessage tone="loading" title="Loading the livestock inventory report…" />
        )}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the livestock inventory report."
            detail={error ?? undefined}
          />
        )}

        {isEmpty && (
          <StateMessage
            tone="empty"
            title="No livestock on file."
            detail="Herds and animals you record will show up here."
          />
        )}

        {status === "ready" && rows.length > 0 && (
          <>
            <div
              className="livestock-inventory__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="livestock-inventory-heading"
            >
              <table className="livestock-inventory__table ruled-rows">
                <caption className="livestock-inventory__caption">
                  {rows.length} {rows.length === 1 ? "line" : "lines"} · {report?.activeTotal ?? 0} active head
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Herd</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="livestock-inventory__num">Count</th>
                    <th scope="col">Last transaction</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={`${row.herdName}-${row.status}`}>
                      <th scope="row" className="livestock-inventory__herd">{row.herdName}</th>
                      <td>{row.status}</td>
                      <td className="livestock-inventory__num">{row.count}</td>
                      <td>{row.lastTransactionDate ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="livestock-inventory__export">
              <h3 className="livestock-inventory__export-title">Livestock inventory for your records</h3>
              <p className="livestock-inventory__export-copy">
                The same rows above, as a CSV a spreadsheet can open.
              </p>
              <button
                type="button"
                className="btn btn--secondary livestock-inventory__export-btn"
                disabled={csvState.kind === "busy"}
                onClick={() => void downloadCsv()}
              >
                {csvState.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {csvState.kind === "done" && (
                <p className="livestock-inventory__export-result" role="status">
                  {csvState.rows} {csvState.rows === 1 ? "line" : "lines"} saved.
                </p>
              )}

              {csvState.kind === "error" && (
                <p className="livestock-inventory__export-error" role="alert">
                  {csvState.message} Nothing was downloaded.
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
